import { Box, Typography } from "@mui/material";
import { BarPlot } from "@mui/x-charts/BarChart";
import { ChartsGrid } from "@mui/x-charts/ChartsGrid";
import { ChartsReferenceLine } from "@mui/x-charts/ChartsReferenceLine";
import { ChartsTooltip } from "@mui/x-charts/ChartsTooltip";
import { ChartsXAxis } from "@mui/x-charts/ChartsXAxis";
import { ChartsYAxis } from "@mui/x-charts/ChartsYAxis";
import { LineHighlightPlot, LinePlot, MarkPlot } from "@mui/x-charts/LineChart";
import { ResponsiveChartContainer } from "@mui/x-charts/ResponsiveChartContainer";

interface LensTimelineLineChartProps {
  labels: string[];
  avgScores: (number | null)[];
  minScores: (number | null)[];
  maxScores: (number | null)[];
  countScores: (number | null)[];
  avgScores5dMA: (number | null)[];
  teamBest: number | null;
  teamAvg: number | null;
}

const LensTimelineLineChart = ({
  labels,
  avgScores,
  minScores,
  maxScores,
  countScores,
  teamBest,
  teamAvg
}: LensTimelineLineChartProps) => {
  if (labels.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
        <Typography variant="body2" color="text.secondary">
          No data to display
        </Typography>
      </Box>
    );
  }

  const adjustedMaxScores = maxScores.map((score, index) => {
    return score === null ? null : score - (minScores[index] ?? 0);
  });

  return (
    <>
      <Box sx={{ mb: 2, display: "flex", gap: 3, alignItems: "center", flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{
            width: 30,
            height: 0,
            borderTop: "2px dashed #2e7d32"
          }} />
          <Typography variant="caption" color="text.secondary">
            Best (time range average)
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{
            width: 30,
            height: 0,
            borderTop: "2px dashed #d32f2f"
          }} />
          <Typography variant="caption" color="text.secondary">
            Team Avg (time range average)
          </Typography>
        </Box>
      </Box>
      <ResponsiveChartContainer
        xAxis={[
          {
            scaleType: "band",
            data: labels,
            label: "Date"
          }
        ]}
        yAxis={[
          {
            min: 0,
            max: 5,
            label: "Score"
          }
        ]}
        series={[
          {
            data: avgScores,
            connectNulls: true,
            label: "Average Score",
            color: "#1976d2",
            id: "avgScoreLine",
            type: "line"
          },
          {
            data: minScores,
            label: "Min Score",
            color: "#ffffff00",
            id: "minScoreLine",
            type: "bar",
            stack: "minmax"
          },
          {
            data: adjustedMaxScores,
            label: "Max Score",
            color: "#1976d23d",
            id: "maxScoreLine",
            type: "bar",
            stack: "minmax",
            valueFormatter: (__, context) => {
              const actualValue = maxScores[context.dataIndex];
              return `${actualValue?.toFixed(2) || "-"}`;
            }
          },
          {
            data: countScores,
            label: "# Scored",
            color: "#ffffff00",
            id: "countScoreLine",
            type: "line",
            showMark: false
          }

        ]}
        sx={{
          ["& .MuiLineElement-series-minScoreLine"]: {
            strokeWidth: 1
          },
          ["& .MuiLineElement-series-maxScoreLine"]: {
            strokeWidth: 1
          },
          ["& .MuiLineElement-series-avgScoreLine"]: {
            strokeWidth: 4
          },
          ["& .MuiLineElement-series-avgScore5dMALine"]: {
            strokeWidth: 2
          },
          ["& .MuiMarkElement-series-minScoreLine"]: {
            r: 2,
            strokeWidth: 1
          },
          ["& .MuiMarkElement-series-maxScoreLine"]: {
            r: 2,
            strokeWidth: 1
          },
          ["& .MuiBarElement-series-maxScoreLine"]: {
            rx: 12
          }
        }}
        margin={{ top: 20, right: 20, bottom: 70, left: 60 }}
      >
        <LinePlot />
        <BarPlot
          borderRadius={8}
        />
        <MarkPlot />
        <ChartsReferenceLine y={teamBest ?? 0}
          label="Best"
          labelAlign="end"
          lineStyle={{ stroke: "#2e7d32", strokeWidth: 2, strokeDasharray: "5 5" }} />
        <ChartsReferenceLine
          y={teamAvg ?? 0}
          label="Team Avg"
          labelAlign="end"
          lineStyle={{ stroke: "#d32f2f", strokeWidth: 2, strokeDasharray: "5 5" }} />
        <ChartsXAxis />
        <ChartsYAxis
          disableLine
        />
        <LineHighlightPlot />
        <ChartsTooltip />
        <ChartsGrid
          horizontal={true}
        />
      </ResponsiveChartContainer>
    </>
  );
};

export default LensTimelineLineChart;
